<template>
    <BaseMapShell v-model:panelOpen="panelOpen" v-model:rightPanelOpen="rightPanelOpen" :center="center" :zoom="zoom"
        :bounds="bounds" :max-bounds="maxBounds" :minZoom="3" :maxZoom="19">
        <template v-if="mission">
            <BaliseMarker v-for="balise in mission.balises" :key="balise.id" :balise="balise" :followMode="false"
                :warning="true" :warningColor="balise.ongoingAlarm ? 'red' : 'blue'"
                @click="openRightPanel(balise)" />
            <BaliseMarker v-if="mission.transporteur" :balise="mission.transporteur" :followMode="false"
                :warning="true" :warningColor="mission.transporteur.ongoingAlarm ? 'red' : 'blue'"
                @click="openRightPanel(mission.transporteur)" />
            <CorridoringDisplay v-if="mission?.corridoring?.id" :corridoringId="mission.corridoring.id"
                @loaded="onCorridoringLoaded" />
        </template>

        <template #right-panel>
            <RightVehiclePicker v-if="rightPanelMode === 'pick'" :balises="nearbyBalises" @select="openDetail" />
            <RightVehiclePanel v-else-if="selectedBalise" :balise="selectedBalise" :followMode="false"
                @follow="followMarker" @setParking="setParking" @removeParking="removeParking"
                @vehicleReport="onVehicleReport" />
            <div v-else-if="vehiclePanelError" class="small">
                <p class="text-danger">{{ vehiclePanelError }}</p>
                <button type="button" class="btn btn-sm btn-outline-primary"
                    @click="loadVehicleDetail(selectedVehicleId)">Réessayer</button>
            </div>
            <p v-else class="text-muted small">Chargement des informations du véhicule…</p>
        </template>
    </BaseMapShell>
</template>

<script>
import CorridoringDisplay from "../Components/CorridoringDisplay.vue";
import BaliseMarker from "../Components/BaliseMarker.vue";
import RightVehiclePanel from "../Components/rightPanel/RightVehiclePanel.vue";
import RightVehiclePicker from "../Components/rightPanel/RightVehiclePicker.vue";
import MissionRepository from '@/repositories/mission.repo.js';
import BaliseRepository from '@/repositories/balise.repo.js';
import BaliseService from '@/services/balise.service.js';
import ReportService from '@/services/report.service.js';
import BaseMapShell from "../BaseMapShell.vue";
import { computePaddedBounds } from "../composables/useBounds";

export default {
    components: {
        BaseMapShell,
        CorridoringDisplay,
        BaliseMarker,
        RightVehiclePanel,
        RightVehiclePicker,
    },
    data() {
        return {
            panelOpen: false,
            rightPanelOpen: false,
            rightPanelMode: 'detail',
            selectedBalise: null,
            selectedVehicleId: null,
            nearbyBalises: [],
            vehiclePanelError: null,
            vehicleLoadRequest: 0,
            zoom: 13,
            center: [48.9571487, 2.1941876],
            timer: '',
            bounds: [],
            maxBounds: [],
            mission: null,
        };
    },
    created() {
        this.refreshData();
        this.timer = setInterval(this.refreshData, 5000);
    },
    beforeUnmount() {
        clearInterval(this.timer);
        this.vehicleLoadRequest++;
    },
    methods: {
        async refreshData() {
            try {
                this.mission = await MissionRepository.getMission(this.$route.params.id);
                if (this.rightPanelOpen && this.rightPanelMode === 'detail' && this.selectedBalise) {
                    this.loadVehicleDetail(this.selectedVehicleId);
                }
            } catch (e) {
                console.error('Erreur lors du rafraîchissement de la mission :', e);
            }
        },
        getMissionBalises() {
            const balises = [
                ...(Array.isArray(this.mission?.balises) ? this.mission.balises : []),
                this.mission?.transporteur,
            ];
            const byId = new Map();
            for (const balise of balises) {
                if (balise?.id != null) byId.set(String(balise.id), balise);
            }
            return Array.from(byId.values());
        },
        findNearbyBalises(target, radiusMeters = 25) {
            const lat1 = Number(target?.lastLatitude);
            const lon1 = Number(target?.lastLongitude);
            if (target?.lastLatitude == null || target?.lastLongitude == null
                || !Number.isFinite(lat1) || !Number.isFinite(lon1)) return [target];

            const toRad = degrees => degrees * Math.PI / 180;
            const radius = 6371000;
            return this.getMissionBalises()
                .filter(balise => {
                    const lat2 = Number(balise.lastLatitude);
                    const lon2 = Number(balise.lastLongitude);
                    if (balise.lastLatitude == null || balise.lastLongitude == null
                        || !Number.isFinite(lat2) || !Number.isFinite(lon2)) return false;

                    const dLat = toRad(lat2 - lat1);
                    const dLon = toRad(lon2 - lon1);
                    const a = Math.sin(dLat / 2) ** 2
                        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
                    return 2 * radius * Math.asin(Math.sqrt(a)) <= radiusMeters;
                })
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        },
        openRightPanel(balise) {
            const candidates = this.findNearbyBalises(balise);
            if (candidates.length > 1) {
                this.vehicleLoadRequest++;
                this.nearbyBalises = candidates.map(candidate => ({
                    ...candidate,
                    immat: candidate.immat ?? candidate.registrationPlate ?? '',
                }));
                this.rightPanelMode = 'pick';
                this.selectedBalise = null;
                this.selectedVehicleId = null;
                this.vehiclePanelError = null;
            } else {
                this.openDetail(candidates[0] || balise);
            }
            this.rightPanelOpen = true;
        },
        openDetail(balise) {
            if (balise?.id == null) return;
            this.selectedVehicleId = balise.id;
            this.selectedBalise = null;
            this.vehiclePanelError = null;
            this.rightPanelMode = 'detail';
            this.rightPanelOpen = true;
            this.loadVehicleDetail(balise.id);
        },
        async loadVehicleDetail(baliseId) {
            if (baliseId == null) return;
            const requestId = ++this.vehicleLoadRequest;
            try {
                // La réponse de mission n'est pas supposée contenir toutes les données
                // nécessaires aux commandes et au panneau véhicule.
                const balise = await BaliseRepository.getBalise(baliseId);
                if (requestId !== this.vehicleLoadRequest || !this.rightPanelOpen
                    || this.rightPanelMode !== 'detail' || String(this.selectedVehicleId) !== String(baliseId)) return;
                if (!balise) throw new Error('Véhicule introuvable');
                this.selectedBalise = balise;
                this.vehiclePanelError = null;
            } catch (e) {
                if (requestId !== this.vehicleLoadRequest || !this.rightPanelOpen
                    || this.rightPanelMode !== 'detail' || String(this.selectedVehicleId) !== String(baliseId)) return;
                console.error('Erreur chargement véhicule :', e);
                if (!this.selectedBalise) {
                    this.vehiclePanelError = 'Impossible de charger les informations du véhicule.';
                }
            }
        },
        followMarker(baliseId) {
            this.$router.push({ name: 'MapFollowBalise', params: { bid: baliseId } });
        },
        async setParking(baliseId) {
            try {
                await BaliseService.setParking(baliseId);
                await this.loadVehicleDetail(baliseId);
            } catch (e) {
                console.error('Erreur activation stationnement :', e);
            }
        },
        async removeParking(baliseId) {
            try {
                await BaliseService.removeParking(baliseId);
                await this.loadVehicleDetail(baliseId);
            } catch (e) {
                console.error('Erreur désactivation stationnement :', e);
            }
        },
        async onVehicleReport({ id, from, to }) {
            try {
                const resp = await ReportService.getVehicleReport(id, from, to);
                const { downloadUrl } = resp.data || {};
                if (!downloadUrl) throw new Error('downloadUrl manquant');

                const apiBase = process.env.VUE_APP_API_URL;
                const origin = new URL(apiBase).origin;
                const absolute = new URL(downloadUrl, origin).toString();

                const link = document.createElement('a');
                link.href = absolute;
                link.target = '_blank';
                link.rel = 'noopener';
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (e) {
                console.error('Erreur lors du téléchargement du rapport véhicule :', e);
            }
        },
        onCorridoringLoaded(positions) {
            // Bounds robustes (>= 2 points) sinon on reste sur center/zoom
            this.$nextTick(() => {
                if (Array.isArray(positions) && positions.length >= 2) {
                    this.bounds = computePaddedBounds(positions);
                } else if (Array.isArray(positions) && positions.length === 1) {
                    this.bounds = [];
                    this.center = [positions[0].latitude, positions[0].longitude];
                    this.zoom = 15;
                } else {
                    this.bounds = [];
                }
            });
        },
    }
}
</script>
